import { Box } from "arkh";
import React, {
  useRef,
  useCallback,
  useImperativeHandle,
  Suspense,
  useMemo,
} from "react";
import { ActivityIndicator, Alert, StyleSheet, View } from "react-native";
import { BottomSheetModal } from "@gorhom/bottom-sheet";
import { useLayout } from "../../TodoCreate/useLayout";
import {
  graphql,
  loadQuery,
  PreloadedQuery,
  useMutation,
  useRelayEnvironment,
} from "react-relay";
import { ErrorBoundary } from "react-error-boundary";
import { ErrorFallback } from "../../../components/mobile/QueryNetworkBoundrary";
import { useReportIssueBottomSheetLayout } from "../useReportIssueBottomSheetLayout";
import { TodoBottomSheetContent } from "./TodoBottomSheetContent";

import { TodoBottomSheetMainScreenQuery } from "./__generated__/TodoBottomSheetMainScreenQuery.graphql";
import { TODO_BOTTOM_SHEET_MAIN_SCREEN_QUERY } from "./TodoBottomSheetMainScreenQuery";
import { TodoBottomSheetCreateMutation } from "./__generated__/TodoBottomSheetCreateMutation.graphql";
import { usePersistedEmployeeId } from "@/src/OS/Login/usePersistedEmployeeId";
import {
  SafeAreaInsetsContext,
  useSafeAreaInsets,
} from "react-native-safe-area-context";
import { TodoBottomSheetHeader } from "./getTodoBottomSheetScreenOptions";

type TodoBottomSheetProps = {
  topSnapPoint?: number | string;
  position?: "left" | "right";
  containerWidth?: number;
  onDismiss?: () => void;
  openExpanded?: boolean;
  openTodoEditCategory: (todoId: string) => void;
  openProductDetail: (productId: string) => void;
  onShowFile: (id: string) => void;
  ref: React.RefObject<ReportIssueBottomSheetRef | null>;
};

type ReportIssueBottomSheetRef = {
  close: () => void;
  present: (newlyCreated: boolean, issueId: string) => void;
  createIssue: (taskExecutionId: string, taskTitle: string) => void;
};

export const TodoBottomSheet = (props: TodoBottomSheetProps) => {
  const env = useRelayEnvironment();
  const ref = useRef<BottomSheetModal>(null);
  const retainedQueries = useRef<
    PreloadedQuery<TodoBottomSheetMainScreenQuery>[]
  >([]);

  const activeQuery =
    useRef<null | PreloadedQuery<TodoBottomSheetMainScreenQuery>>(null);

  const retryRef = useRef(() => {});

  const { currentEmployeeId } = usePersistedEmployeeId();
  const [addTodo, isTodoInFlight] = useMutation<TodoBottomSheetCreateMutation>(
    graphql`
      mutation TodoBottomSheetCreateMutation(
        $taskExecutionId: ID!
        $employeeId: ID
      ) {
        createTodoItem(
          todo: { taskExecutionId: $taskExecutionId }
          employeeId: $employeeId
        ) {
          todo {
            id
            title
            subject {
              breadcrumbs
              icon
            }
            taskExecution {
              id
              todos {
                id
                ...RoutineOccasionTodoIssue_todo
                ...TodoListItem_todo
              }
            }
          }
        }
      }
    `
  );

  const present = useCallback(
    (newlyCreated: boolean, todoId: string) => {
      retryRef.current = () => {
        present(newlyCreated, todoId);
      };
      if (activeQuery.current) {
        retainedQueries.current.push(activeQuery.current);
      }

      activeQuery.current = loadQuery<TodoBottomSheetMainScreenQuery>(
        env,
        TODO_BOTTOM_SHEET_MAIN_SCREEN_QUERY,
        { todoId },
        { fetchPolicy: "store-and-network" }
      );

      env.commitUpdate((store) => {
        store
          .getRoot()
          ?.getLinkedRecord("store")
          ?.setValue(todoId, "activeIssueId");
      });

      ref.current?.present({
        query: activeQuery.current,
        mode: newlyCreated ? "create" : "edit",
        issueId: todoId,
      });
    },
    [env]
  );
  const shouldShowCreated = useRef(false);

  const createTodo = useCallback(
    (taskExecutionId: string, taskTitle: string) => {
      retryRef.current = () => {};
      if (isTodoInFlight) {
        return;
      }
      shouldShowCreated.current = true;

      ref.current?.present({ mode: "creating", taskTitle });

      addTodo({
        variables: {
          taskExecutionId,
          employeeId: currentEmployeeId,
        },

        onError: () => {
          Alert.alert("Error", "Failed to create issue");
          shouldShowCreated.current = false;
          setTimeout(() => {
            ref.current?.forceClose();
          }, 100);
        },
        onCompleted: (d) => {
          if (shouldShowCreated.current && d.createTodoItem.todo.id) {
            present(true, d.createTodoItem.todo.id);
          }
          shouldShowCreated.current = false;
        },
      });
    },
    [addTodo, currentEmployeeId, isTodoInFlight, present]
  );

  const close = useCallback(() => {
    ref.current?.dismiss();
  }, []);

  useImperativeHandle(props.ref, () => ({
    present,
    createIssue: createTodo,
    close,
  }));

  const [{ width }, onLayout] = useLayout();

  const onDismiss = useCallback(() => {
    shouldShowCreated.current = false;
    if (retainedQueries.current.length > 0) {
      retainedQueries.current.forEach((q) => q.dispose());
      retainedQueries.current = [];
    }
    env.commitUpdate((store) => {
      const s = store.getRoot()?.getLinkedRecord("store");

      const currentId = s?.getValue("activeIssueId");
      if (currentId && currentId === activeQuery.current?.variables?.todoId) {
        s?.setValue(null, "activeIssueId");
      }
    });

    activeQuery.current?.dispose();
    activeQuery.current = null;
  }, [env]);

  const { style, renderBackdrop, snapPoints, detached } =
    useReportIssueBottomSheetLayout({
      openExpanded: props.openExpanded,
      containerWidth: props.containerWidth,
      topSnapPoint: props.topSnapPoint,
      position: props.position,
      width,
    });

  const insets = useSafeAreaInsets();
  const Placeholder = useMemo(
    () => (
      <Box>
        <TodoBottomSheetHeader
          headerLeft="DONE"
          title=""
          onDone={() => {
            ref.current?.close();
          }}
        />
        <ActivityIndicator />
      </Box>
    ),
    []
  );

  const renderContent = useCallback(
    (args: {
      data?:
        | {
            query: PreloadedQuery<TodoBottomSheetMainScreenQuery>;
            mode: "edit" | "create";
            issueId: string;
          }
        | {
            mode: "creating";
            taskTitle: string;
          };
    }) => {
      if (!args.data) {
        throw new Error("No args to todo bottom sheet content");
      }

      return (
        <SafeAreaInsetsContext.Provider value={{ ...insets, top: 0 }}>
          {args.data.mode === "creating" ? (
            Placeholder
          ) : (
            <Suspense fallback={Placeholder}>
              <ErrorBoundary
                onReset={() => {
                  retryRef.current();
                }}
                FallbackComponent={ErrorFallback}
              >
                <TodoBottomSheetContent
                  onShowFile={props.onShowFile}
                  mainQuery={args.data.query}
                  todoId={args.data.issueId}
                  mode={args.data.mode}
                  openTodoMoveToCategory={props.openTodoEditCategory}
                  openProductDetail={props.openProductDetail}
                />
              </ErrorBoundary>
            </Suspense>
          )}
        </SafeAreaInsetsContext.Provider>
      );
    },
    [
      insets,
      Placeholder,
      props.onShowFile,
      props.openTodoEditCategory,
      props.openProductDetail,
    ]
  );

  return (
    <View
      style={{
        ...StyleSheet.absoluteFillObject,
      }}
      pointerEvents="none"
      onLayout={onLayout}
    >
      <BottomSheetModal
        backdropComponent={renderBackdrop}
        enablePanDownToClose
        enableDismissOnClose
        stackBehavior="replace"
        snapPoints={snapPoints}
        enableDynamicSizing={false}
        keyboardBehavior="extend"
        keyboardBlurBehavior="none"
        android_keyboardInputMode="adjustPan"
        onDismiss={onDismiss}
        style={style}
        detached={detached}
        ref={ref}
      >
        {renderContent}
      </BottomSheetModal>
    </View>
  );
};
